import {
  ByteSizes,
  ValidationError,
  getReadableFileSize,
  validateFileSize,
  validatedFileType,
} from "@validereinc/utilities";
import { isPlainObject } from "es-toolkit/compat";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { mergeProps } from "../../utils/component-props";
import { usePrevious } from "../../utils/hooks/usePrevious";
import { InputWithLabels } from "../_components/InputWithLabels";
import FileInput from "./FileInput";
import { FileUploadCard } from "./FileUploadCard";
import {
  FileExtended,
  getFileForUploadedFile,
} from "./FileUploadInput.helpers";
import {
  FileUploadInputContextProvider,
  useFileUploadInputContext,
} from "./FileUploadInputContext";
import type {
  ControlledFileUploadInputProps,
  FileUploadInputProps,
  FileUploadedRefType,
} from "./types";

const FileUploadInput = <T extends FileUploadedRefType = FileUploadedRefType>({
  value,
  onChange,
  uploadFile,
  downloadFile,
  isLoading,
  isBusy,
  ...restProps
}: FileUploadInputProps<T>) => {
  const {
    files,
    setFiles,
    setAbortedFiles,
    areFilesBeingDownloaded,
    areFilesBeingUploaded,
  } = useFileUploadInputContext<T>();
  const previousValue = usePrevious(value);

  const handleOnChange = (fileToUpdate: {
    fileExtended: FileExtended<T>;
    action: "delete" | "cancel" | "update" | "add";
  }) => {
    switch (fileToUpdate.action) {
      case "delete":
        setFiles((latestFiles) => {
          const newFiles = latestFiles.filter(
            (f) => f.id !== fileToUpdate.fileExtended.id
          );

          onChange?.(newFiles.map((f) => f.fileUploaded).filter((f) => !!f));
          return newFiles;
        });
        break;
      case "cancel":
        setAbortedFiles((latestFiles) =>
          latestFiles.filter((f) => f.abortController.signal.aborted)
        );
        setFiles((latestFiles) => {
          const newFiles = latestFiles.filter(
            (f) =>
              !f.abortController.signal.aborted &&
              f.id !== fileToUpdate.fileExtended.id
          );

          onChange?.(newFiles.map((f) => f.fileUploaded).filter((f) => !!f));
          return newFiles;
        });
        break;
      case "update":
        setFiles((latestFiles) => {
          const newFiles = latestFiles.map((f) =>
            f.id === fileToUpdate.fileExtended.id
              ? fileToUpdate.fileExtended
              : f
          );

          onChange?.(newFiles.map((f) => f.fileUploaded).filter((f) => !!f));
          return newFiles;
        });
        break;
      case "add":
        setFiles((latestFiles) => {
          const newFiles = [...latestFiles, fileToUpdate.fileExtended];

          // For newly added files, we need to notify the parent immediately
          // with all files that have been uploaded (excluding newly added ones that haven't been uploaded yet)
          onChange?.(newFiles.map((f) => f.fileUploaded).filter((f) => !!f));
          return newFiles;
        });
        break;
    }
  };

  // on initial render, the value is not set yet, but the internal files state needs to be set
  useEffect(() => {
    if (!!value?.length && !files?.length) {
      setFiles(value.map((fu) => new FileExtended(null, { fileUploaded: fu })));
    }
  }, [value, files]);

  // when the value is cleared, the internal files state needs to be cleared too
  useEffect(() => {
    if (
      previousValue?.length &&
      !value?.length &&
      files?.length &&
      files.every((f) => f.percentageProcessed === 100)
    ) {
      setFiles([]);
      onChange?.([]);
    }
  }, [value, previousValue, files, onChange]);

  const memoizedFileUploadCards = useCallback(() => {
    return (
      files?.map((fe) => (
        <FileUploadCard<T>
          key={fe.id}
          id={fe.id}
          file={fe.file}
          fileUploaded={fe.fileUploaded}
          percentageProcessed={fe.percentageProcessed}
          percentageProcessedLabel={fe.percentageProcessedLabel}
          error={fe.error}
          startedProcessing={fe.startedProcessing}
          downloadFile={downloadFile}
          uploadFile={uploadFile}
          isValid={!fe.error}
          isBusy={isBusy}
          onChange={(newData) => {
            // handle delete
            if (!newData) {
              handleOnChange({ fileExtended: fe, action: "delete" });
              return;
            }

            // everything else is an update
            handleOnChange({
              fileExtended: {
                ...fe,
                ...newData,
              },
              action: "update",
            });
            return;
          }}
          abortController={fe.abortController}
          onCancel={() => {
            handleOnChange({ fileExtended: fe, action: "cancel" });
          }}
          onCancelResolved={(fileIdToDelete) =>
            setAbortedFiles((abortedFiles) =>
              abortedFiles.filter((f) => f.id !== fileIdToDelete)
            )
          }
        />
      )) ?? null
    );
  }, [files, value, isBusy, downloadFile, uploadFile, handleOnChange]);

  return (
    <FileInput
      {...restProps}
      // the onChange handler will only return newly selected files, if any
      isAdditive
      // @ts-expect-error purposely send through undefined slots
      value={files?.map((fe) => fe.file ?? undefined) ?? []}
      onChange={(newFiles) => {
        // files chosen through the file input are always added
        // note, the same file can be added multiple times, since the file extended class has a unique id per instance
        newFiles?.forEach((f) => {
          handleOnChange({
            fileExtended: new FileExtended(f, {
              percentageProcessed: 1,
            }),
            action: "add",
          });
        });
      }}
      previewSlot={memoizedFileUploadCards}
      isLoading={isLoading || areFilesBeingDownloaded}
      isBusy={isBusy || areFilesBeingUploaded}
    />
  );
};

FileUploadInput.displayName = "FileUploadInput";

const ControlledFileUploadInputContent = <
  T extends FieldValues,
  U extends FileUploadedRefType = FileUploadedRefType,
>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledFileUploadInputProps<T, U>) => {
  const {
    isDisabled,
    isLoading,
    isBusy,
    acceptedFileTypes,
    placeholder,
    uploadFile,
    downloadFile,
  } = restProps;
  const maxFileSizeInBytes = 500 * ByteSizes.MB;
  const { files, areFilesBeingUploaded, areFilesBeingDownloaded } =
    useFileUploadInputContext();
  const { field, fieldState } = useController({
    name,
    // @ts-expect-error expects a certain shape but it's fine
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "File upload input"} is required`
                        : undefined,
                  }
                : {}),
              required: (value: U[]) => {
                if (
                  !isRequired ||
                  areFilesBeingUploaded ||
                  areFilesBeingDownloaded
                ) {
                  return true;
                }

                const associatedFilesInCache =
                  value?.map((v) => getFileForUploadedFile(v, files)) ?? [];

                if (
                  associatedFilesInCache.length &&
                  associatedFilesInCache.every((f) => !!f)
                ) {
                  return true;
                }

                return "File is required";
              },
              size: (value: U[]) => {
                if (!value || areFilesBeingUploaded || areFilesBeingDownloaded)
                  return true;

                let rootValidationMsg = "";

                for (const uploadedFile of value) {
                  const associatedFile = getFileForUploadedFile(
                    uploadedFile,
                    files
                  );

                  if (!associatedFile) continue;

                  const validation = validateFileSize(associatedFile.file, {
                    max: maxFileSizeInBytes,
                  });

                  if (typeof validation === "string") {
                    // deliberate mutation
                    associatedFile.error = validation;
                    rootValidationMsg = validation;
                  } else if (!validation) {
                    // deliberate mutation
                    associatedFile.error = "Invalid file size(s)";
                    rootValidationMsg = "Invalid file size(s)";
                  }
                }

                return rootValidationMsg || true;
              },
              type: (value: U[]) => {
                if (!value || areFilesBeingUploaded || areFilesBeingDownloaded)
                  return true;

                let rootValidationMsg = "";

                for (const uploadedFile of value) {
                  const associatedFile = getFileForUploadedFile(
                    uploadedFile,
                    files
                  );

                  if (!associatedFile) continue;

                  const validation = validatedFileType(
                    associatedFile.file,
                    acceptedFileTypes
                  );

                  if (typeof validation === "string") {
                    // deliberate mutation
                    associatedFile.error = validation;
                    rootValidationMsg = validation;
                  } else if (!validation) {
                    // deliberate mutation
                    associatedFile.error = "Invalid file type(s)";
                    rootValidationMsg = "Invalid file type(s)";
                  }
                }

                return rootValidationMsg || true;
              },
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const maxFileSizePlaceholder = `${
    placeholder ?? "Drag a file here"
  } (max. ${getReadableFileSize(maxFileSizeInBytes)})`;
  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  if (!uploadFile || !downloadFile) {
    throw new ValidationError(
      "ControlledFileUploadInput: uploadFile() and downloadFile() props are required."
    );
  }

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot, leftSlot }) => (
        <FileUploadInput<U>
          // @ts-expect-error might be overridden but that's ok
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          flairSlot={flairSlot}
          leftSlot={leftSlot}
          isInline={isInline}
          isFluid={isFluid}
          {...field}
          {...mergedProps}
          placeholder={maxFileSizePlaceholder}
        />
      )}
    </InputWithLabels>
  );
};

export const ControlledFileUploadInput = <
  T extends FieldValues,
  U extends FileUploadedRefType = FileUploadedRefType,
>(
  controlledFileInputProps: ControlledFileUploadInputProps<T, U>
) => {
  return (
    <FileUploadInputContextProvider>
      <ControlledFileUploadInputContent {...controlledFileInputProps} />
    </FileUploadInputContextProvider>
  );
};

ControlledFileUploadInput.displayName = "ControlledFileUploadInput";

export default FileUploadInput;
